/**
 * UserStore - Global user profile management
 * 
 * Fetches active user profiles from Firebase and provides utilities
 * for accessing user data across the application.
 * 
 * Features:
 * - Single fetch on app initialization
 * - Global window.UserStore access for legacy JavaScript
 * - Helper methods for common operations (@mentions, avatar lookup)
 * - Automatic fallbacks for missing data
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/index';

interface SlackProfile {
  id: string;
  real_name: string;
  display_name: string;
  avatar: string;
  color: string;
}

interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  inactive?: boolean;
  slack?: SlackProfile | null;
}

interface GetActiveUserProfilesResponse {
  users: UserProfile[];
  count: number;
  cached: boolean;
  timestamp: string;
  duration_ms: number;
}

interface UserData {
  display_name: string;
  color: string;
  avatar: string;
  email?: string;
  uid?: string;
}

class UserStoreClass {
  private userData: Record<string, UserData> = {};
  private usersByEmail: Record<string, UserData> = {};
  private usersByUid: Record<string, UserData> = {};
  private rawProfiles: UserProfile[] = [];
  private isLoaded = false;
  private loadPromise: Promise<void> | null = null;

  // Cache configuration
  private readonly CACHE_KEY = 'JIDOKA_USER_STORE_CACHE';
  private readonly CACHE_TIMESTAMP_KEY = 'JIDOKA_USER_STORE_TIMESTAMP';
  private readonly CACHE_TTL = 3600000; // 1 hour in milliseconds

  /**
   * Initialize the user store by fetching profiles from Firebase
   * Returns a promise that resolves when data is loaded
   */
  async init(): Promise<void> {
    // Return existing promise if already loading
    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Return immediately if already loaded
    if (this.isLoaded) {
      return Promise.resolve();
    }

    // Try to load from cache first
    if (this._loadFromCache()) {
      // console.log('[UserStore] Loaded from localStorage cache');
      return Promise.resolve();
    }

    // Cache failed or expired - fetch fresh data
    this._clearCache();
    this.loadPromise = this._fetchProfiles();
    return this.loadPromise;
  }

  /**
   * Check if store failed to load and needs retry
   */
  get needsRetry(): boolean {
    return !this.isLoaded && !this.loadPromise;
  }

  /**
   * Check if the store has been initialized (loaded from cache or fetched)
   * Use this to avoid calling getAvatar() or other methods before data is ready
   */
  isInitialized(): boolean {
    return this.isLoaded;
  }

  private buildUserData(user: UserProfile): UserData {
    return {
      display_name: user.slack?.display_name || user.displayName || user.email || user.uid,
      color: user.slack?.color || '#666666',
      avatar: user.slack?.avatar || '/assets/img/avatar.png',
      email: user.email,
      uid: user.uid
    };
  }

  private async _fetchProfiles(): Promise<void> {
    try {
      const getActiveUserProfiles = httpsCallable<void, GetActiveUserProfilesResponse>(
        functions,
        'getActiveUserProfiles'
      );

      const result = await getActiveUserProfiles();
      const users = result.data.users || [];

      // Store raw profiles for advanced queries
      this.rawProfiles = users;

      this.userData = {};
      this.usersByEmail = {};
      this.usersByUid = {};

      // Build indexed lookups
      users.forEach(user => {
        const userData = this.buildUserData(user);
        const primaryKey = user.slack?.real_name || user.displayName || user.email || user.uid;

        if (primaryKey) {
          this.userData[primaryKey] = userData;
        }

        // Preserve the Slack real_name index for legacy lookups when available.
        if (user.slack?.real_name && user.slack.real_name !== primaryKey) {
          this.userData[user.slack.real_name] = userData;
        }

        if (user.email) {
          this.usersByEmail[user.email.toLowerCase()] = userData;
        }

        if (user.uid) {
          this.usersByUid[user.uid] = userData;
        }
      });

      this.isLoaded = true;

      // Cache the data to localStorage
      this._saveToCache();

      // console.log(`[UserStore] Loaded ${users.length} user profiles (${Object.keys(this.userData).length} with Slack data)`);
    } catch (error) {
      console.error('[UserStore] Error loading user profiles:', error);
      this.isLoaded = false;
      this.loadPromise = null;
      throw error;
    }
  }

  /**
   * Load user data from localStorage cache
   * Returns true if cache was valid and loaded successfully
   */
  private _loadFromCache(): boolean {
    try {
      const cachedData = localStorage.getItem(this.CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);

      if (!cachedData || !cachedTimestamp) {
        return false;
      }

      const timestamp = parseInt(cachedTimestamp, 10);
      const now = Date.now();

      // Check if cache has expired
      if (now - timestamp > this.CACHE_TTL) {
        // console.log('[UserStore] Cache expired, fetching fresh data');
        this._clearCache();
        return false;
      }

      const parsed = JSON.parse(cachedData);

      // Restore all data structures
      this.rawProfiles = parsed.rawProfiles || [];
      this.userData = parsed.userData || {};
      this.usersByEmail = parsed.usersByEmail || {};
      this.usersByUid = parsed.usersByUid || {};
      this.isLoaded = true;

      return true;
    } catch (error) {
      console.error('[UserStore] Error loading from cache:', error);
      this._clearCache();
      return false;
    }
  }

  /**
   * Save user data to localStorage cache
   */
  private _saveToCache(): void {
    try {
      const cacheData = {
        rawProfiles: this.rawProfiles,
        userData: this.userData,
        usersByEmail: this.usersByEmail,
        usersByUid: this.usersByUid
      };

      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
      localStorage.setItem(this.CACHE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('[UserStore] Error saving to cache:', error);
    }
  }

  /**
   * Clear the localStorage cache
   */
  private _clearCache(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
      localStorage.removeItem(this.CACHE_TIMESTAMP_KEY);
    } catch (error) {
      console.error('[UserStore] Error clearing cache:', error);
    }
  }

  /**
   * Force refresh user data from server (bypasses cache)
   */
  async refresh(): Promise<void> {
    this._clearCache();
    this.isLoaded = false;
    this.loadPromise = null;
    return this.init();
  }

  /**
   * Get user data by Slack real_name (e.g., "john.doe")
   * Compatible with legacy code pattern
   */
  getUserByName(realName: string): UserData | null {
    return this.userData[realName] || null;
  }

  /**
   * Get user data by email address
   */
  getUserByEmail(email: string): UserData | null {
    return this.usersByEmail[email.toLowerCase()] || null;
  }

  /**
   * Get user data by Firebase UID
   */
  getUserByUid(uid: string): UserData | null {
    return this.usersByUid[uid] || null;
  }

  /**
   * Get all user data (legacy format)
   * Returns object keyed by Slack real_name
   */
  getAllUsers(): Record<string, UserData> {
    return this.userData;
  }

  /**
   * Get all raw user profiles (full data)
   */
  getAllProfiles(): UserProfile[] {
    return this.rawProfiles;
  }

  /**
   * Search users for @mention autocomplete
   * Searches display_name, real_name, and email
   */
  searchUsers(query: string): UserData[] {
    const lowerQuery = query.toLowerCase();
    const seenUids = new Set<string>();

    return this.rawProfiles
      .filter(user => {
        return (
          (user.slack?.display_name && user.slack.display_name.toLowerCase().includes(lowerQuery))
          || (user.slack?.real_name && user.slack.real_name.toLowerCase().includes(lowerQuery))
          || (user.displayName && user.displayName.toLowerCase().includes(lowerQuery))
          || (user.email && user.email.toLowerCase().includes(lowerQuery))
        );
      })
      .map(user => this.buildUserData(user))
      .filter(user => {
        const dedupeKey = user.uid || user.email || user.display_name;
        if (seenUids.has(dedupeKey)) {
          return false;
        }

        seenUids.add(dedupeKey);
        return true;
      });
  }

  /**
   * Get display name with fallback
   * Safe for undefined/null input
   */
  getDisplayName(identifier: string | undefined | null): string {
    if (!identifier) return 'User';

    // Try as real_name first
    let user = this.getUserByName(identifier);

    // Try as email
    if (!user && identifier.includes('@')) {
      user = this.getUserByEmail(identifier);
    }

    // Try as UID
    if (!user) {
      user = this.getUserByUid(identifier);
    }

    return user?.display_name || identifier || 'User';
  }

  /**
   * Get avatar URL with fallback
   * Safe for undefined/null input - always returns a valid URL
   */
  getAvatar(identifier: string | undefined | null): string {
    const defaultAvatar = '/assets/img/avatar.png';
    if (!identifier) return defaultAvatar;

    const user = this.getUserByName(identifier) ||
      this.getUserByEmail(identifier) ||
      this.getUserByUid(identifier);

    return user?.avatar || defaultAvatar;
  }

  /**
   * Get user color for badges/avatars
   * Safe for undefined/null input
   */
  getColor(identifier: string | undefined | null): string {
    if (!identifier) return '#666666';

    const user = this.getUserByName(identifier) ||
      this.getUserByEmail(identifier) ||
      this.getUserByUid(identifier);

    return user?.color || '#666666';
  }

  /**
   * Check if store is loaded
   */
  get loaded(): boolean {
    return this.isLoaded;
  }

  /**
   * Get count of loaded users
   */
  get count(): number {
    return Object.keys(this.userData).length;
  }
}

// Create singleton instance
const UserStore = new UserStoreClass();

// Expose to window for legacy JavaScript access
declare global {
  interface Window {
    UserStore: UserStoreClass;
  }
}

window.UserStore = UserStore;

export { UserStore, UserStoreClass };
export type { UserProfile, UserData, SlackProfile };
